import React, { useState } from 'react';
import { collection, addDoc, doc, setDoc, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { db, auth } from '../../firebase/firebaseConfig';
import { createStoreModel, createProductModel } from '../../data/marketplaceModels';
import { ROLES } from '../../utils/roleManager';

const SeedDataGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [generatedCount, setGeneratedCount] = useState({ stores: 0, users: 0, products: 0 });

  // ข้อมูลตัวอย่างสำหรับร้านค้า
  const storeTemplates = [
    {
      name: 'ร้านกัญชาคุณภาพพรีเมียม',
      description: 'จำหน่ายกัญชาคุณภาพสูง สายพันธุ์แท้ 100% ปลอดภัย ตรวจสอบคุณภาพแล้ว',
      category: 'Flowers',
      phone: '0812345678',
      email: 'premium@example.com',
      address: '123 ถนนสุขุมวิท กรุงเทพมหานคร 10110'
    },
    {
      name: 'Green Farm เกษตรอินทรีย์',
      description: 'กัญชาเกษตรอินทรีย์ ปลูกเอง ปลอดสารเคมี ราคาเป็นมิตร',
      category: 'Flowers',
      phone: '0823456789',
      email: 'greenfarm@example.com',
      address: '456 ถนนพหลโยธิน เชียงใหม่ 50000'
    },
    {
      name: 'CBD Shop ครบวงจร',
      description: 'จำหน่ายผลิตภัณฑ์ CBD ครบวงจร น้ำมัน CBD, ครีม, แคปซูล',
      category: 'CBD Products',
      phone: '0834567890',
      email: 'cbdshop@example.com',
      address: '789 ถนนรัชดาภิเษก กรุงเทพมหานคร 10400'
    },
    {
      name: 'Seed Bank Thailand',
      description: 'เมล็ดพันธุ์กัญชาคุณภาพสูง สายพันธุ์หลากหลาย จากต่างประเทศ',
      category: 'Seeds',
      phone: '0845678901',
      email: 'seedbank@example.com',
      address: '321 ถนนสีลม กรุงเทพมหานคร 10500'
    },
    {
      name: 'Edibles Paradise',
      description: 'ขนมกัญชา คุกกี้ บราวนี่ กัมมี่ รสชาติอร่อย โดสแม่นยำ',
      category: 'Edibles',
      phone: '0856789012',
      email: 'edibles@example.com',
      address: '654 ถนนเพชรบุรี กรุงเทพมหานคร 10400'
    },
    {
      name: 'Pre-Roll Express',
      description: 'บุหรี่กัญชาพร้อมสูบ คุณภาพดี ห่อสวย ส่งด่วน',
      category: 'Pre Rolls',
      phone: '0867890123',
      email: 'preroll@example.com',
      address: '987 ถนนลาดพร้าว กรุงเทพมหานคร 10230'
    },
    {
      name: 'Oil Extract Pro',
      description: 'น้ำมันกัญชาเข้มข้น สกัดเย็น 100% ธรรมชาติ',
      category: 'Cannabis Oils',
      phone: '0878901234',
      email: 'oilextract@example.com',
      address: '147 ถนนบางนา กรุงเทพมหานคร 10260'
    },
    {
      name: 'Cannabis Care Center',
      description: 'ศูนย์ดูแลกัญชา ปรึกษาฟรี บริการครบวงจร',
      category: 'Flowers',
      phone: '0889012345',
      email: 'carecenter@example.com',
      address: '258 ถนนอโศก กรุงเทพมหานคร 10110'
    },
    {
      name: 'Organic Green Store',
      description: 'ร้านกัญชาออร์แกนิก ปลอดภัย ตรวจสอบแล้ว',
      category: 'Flowers',
      phone: '0890123456',
      email: 'organic@example.com',
      address: '369 ถนนราชดำริ กรุงเทพมหานคร 10330'
    },
    {
      name: 'Premium Cannabis Hub',
      description: 'ศูนย์รวมกัญชาคุณภาพพรีเมียม สินค้าครบทุกประเภท',
      category: 'Flowers',
      phone: '0901234567',
      email: 'hub@example.com',
      address: '741 ถนนสุขุมวิท กรุงเทพมหานคร 10110'
    }
  ];

  // ข้อมูลตัวอย่างสำหรับสินค้า
  const productTemplates = {
    Flowers: [
      { name: 'กัญชาสายพันธุ์ OG Kush', price: 1500, quantity: 100, unit: 'กรัม', strain: 'OG Kush', type: 'Indica' },
      { name: 'กัญชาสายพันธุ์ Blue Dream', price: 1800, quantity: 80, unit: 'กรัม', strain: 'Blue Dream', type: 'Sativa' },
      { name: 'กัญชาสายพันธุ์ White Widow', price: 2000, quantity: 60, unit: 'กรัม', strain: 'White Widow', type: 'Hybrid' },
      { name: 'กัญชาสายพันธุ์ Purple Haze', price: 1700, quantity: 90, unit: 'กรัม', strain: 'Purple Haze', type: 'Sativa' },
      { name: 'กัญชาสายพันธุ์ Granddaddy Purple', price: 2200, quantity: 50, unit: 'กรัม', strain: 'Granddaddy Purple', type: 'Indica' }
    ],
    Seeds: [
      { name: 'เมล็ดพันธุ์ OG Kush (5 เมล็ด)', price: 2500, quantity: 20, unit: 'ชุด', strain: 'OG Kush', type: 'Indica' },
      { name: 'เมล็ดพันธุ์ Blue Dream (5 เมล็ด)', price: 2800, quantity: 18, unit: 'ชุด', strain: 'Blue Dream', type: 'Sativa' },
      { name: 'เมล็ดพันธุ์ White Widow (5 เมล็ด)', price: 3000, quantity: 15, unit: 'ชุด', strain: 'White Widow', type: 'Hybrid' }
    ],
    Edibles: [
      { name: 'คุกกี้กัญชา (10 ชิ้น)', price: 800, quantity: 30, unit: 'ชุด', strain: '', type: '' },
      { name: 'บราวนี่กัญชา (10 ชิ้น)', price: 900, quantity: 25, unit: 'ชุด', strain: '', type: '' },
      { name: 'กัมมี่กัญชา (20 เม็ด)', price: 1200, quantity: 40, unit: 'ชุด', strain: '', type: '' }
    ],
    'Pre Rolls': [
      { name: 'บุหรี่กัญชา OG Kush (1 มวน)', price: 200, quantity: 100, unit: 'มวน', strain: 'OG Kush', type: 'Indica' },
      { name: 'บุหรี่กัญชา Blue Dream (1 มวน)', price: 220, quantity: 90, unit: 'มวน', strain: 'Blue Dream', type: 'Sativa' },
      { name: 'บุหรี่กัญชา White Widow (1 มวน)', price: 250, quantity: 80, unit: 'มวน', strain: 'White Widow', type: 'Hybrid' }
    ],
    'Cannabis Oils': [
      { name: 'น้ำมันกัญชา CBD 1000mg', price: 3500, quantity: 15, unit: 'ขวด', strain: '', type: '' },
      { name: 'น้ำมันกัญชา THC 500mg', price: 4000, quantity: 12, unit: 'ขวด', strain: '', type: '' },
      { name: 'น้ำมันกัญชา Full Spectrum 1000mg', price: 4500, quantity: 10, unit: 'ขวด', strain: '', type: '' }
    ],
    'CBD Products': [
      { name: 'ครีม CBD บรรเทาปวด', price: 1200, quantity: 25, unit: 'หลอด', strain: '', type: '' },
      { name: 'แคปซูล CBD 25mg (30 แคปซูล)', price: 1800, quantity: 20, unit: 'ขวด', strain: '', type: '' },
      { name: 'น้ำมัน CBD 500mg', price: 2800, quantity: 18, unit: 'ขวด', strain: '', type: '' }
    ]
  };

  // ข้อมูลตัวอย่างสำหรับลูกค้า
  const customerNames = [
    'สมชาย ใจดี', 'สมหญิง รักสุข', 'วิชัย เก่งมาก', 'วิไล สวยงาม', 'ประเสริฐ ดีมาก',
    'ประไพ สุขใจ', 'วิทยา เก่งจริง', 'วิมล สวยจริง', 'ประยุทธ์ ดีจริง', 'ประยงค์ สุขจริง',
    'สมศักดิ์ ใจกว้าง', 'สมหมาย รักงาน', 'วิชัย เก่งงาน', 'วิไล สวยงาน', 'ประเสริฐ ดีงาน',
    'ประไพ สุขงาน', 'วิทยา เก่งดี', 'วิมล สวยดี', 'ประยุทธ์ ดีดี', 'ประยงค์ สุขดี'
  ];

  // สร้างร้านค้าและสินค้า
  const generateStores = async (count) => {
    setLoading(true);
    setProgress('กำลังสร้างร้านค้า...');
    let createdStores = 0;
    let createdProducts = 0;

    try {
      for (let i = 0; i < Math.min(count, storeTemplates.length); i++) {
        const template = storeTemplates[i];
        
        // สร้าง user สำหรับร้าน
        const email = `store${i + 1}@demo.com`;
        const password = 'Demo1234!';
        const displayName = template.name.replace('ร้าน', '').trim();
        
        setProgress(`กำลังสร้างร้าน: ${template.name}...`);
        
        try {
          // สร้าง Firebase Auth user
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          const userId = userCredential.user.uid;
          
          // อัพเดท profile
          await updateProfile(userCredential.user, {
            displayName: displayName
          });
          
          // สร้าง user document
          await setDoc(doc(db, 'users', userId), {
            displayName: displayName,
            email: email,
            role: 'farm',
            storeId: null, // จะอัพเดทหลังจากสร้างร้าน
            hasStore: false,
            tcoins: {
              virtual: Math.floor(Math.random() * 2000), // 0-2000 Bonus Coins
              real: Math.floor(Math.random() * 5000) + 1000 // 1000-6000 T Coins
            },
            createdAt: new Date(),
            updatedAt: new Date()
          });
          
          // สร้างร้าน
          const storeModel = createStoreModel({
            name: template.name,
            description: template.description,
            ownerId: userId,
            ownerRole: 'farm',
            category: template.category,
            contact: {
              phone: template.phone,
              email: template.email,
              address: template.address
            },
            status: 'active',
            reputationScore: Math.floor(Math.random() * 500) + 100, // 100-600
            reputationLevel: Math.floor(Math.random() * 5) + 1, // 1-5
            completedOrders: Math.floor(Math.random() * 100) + 10, // 10-110
            averageRating: (Math.random() * 2 + 3).toFixed(1), // 3.0-5.0
            totalReviews: Math.floor(Math.random() * 50) + 5, // 5-55
            createdAt: new Date(),
            openedAt: new Date()
          });
          
          const storeRef = await addDoc(collection(db, 'stores'), storeModel);
          
          // อัพเดท user document
          await updateDoc(doc(db, 'users', userId), {
            storeId: storeRef.id,
            hasStore: true
          });
          
          createdStores++;
          
          // สร้างสินค้า
          const products = productTemplates[template.category] || productTemplates.Flowers;
          const productCount = Math.min(products.length, 3); // สร้าง 3 สินค้าต่อร้าน
          
          for (let j = 0; j < productCount; j++) {
            const productTemplate = products[j];
            const productModel = createProductModel({
              ...productTemplate,
              storeId: storeRef.id,
              storeName: template.name,
              category: template.category,
              status: 'active',
              views: Math.floor(Math.random() * 500) + 10,
              sales: Math.floor(Math.random() * 20) + 1,
              createdAt: new Date()
            });
            
            await addDoc(collection(db, 'products'), productModel);
            createdProducts++;
          }
          
          // Sign out เพื่อสร้าง user ถัดไป
          await auth.signOut();
          
        } catch (error) {
          console.error(`Error creating store ${i + 1}:`, error);
          // Continue with next store
        }
      }
      
      setProgress(`✅ สร้างร้านค้า ${createdStores} ร้าน และสินค้า ${createdProducts} รายการสำเร็จ!`);
      setGeneratedCount(prev => ({ ...prev, stores: createdStores, products: createdProducts }));
      
    } catch (error) {
      console.error('Error generating stores:', error);
      alert('เกิดข้อผิดพลาดในการสร้างร้านค้า: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // สร้างลูกค้า
  const generateCustomers = async (count) => {
    setLoading(true);
    setProgress('กำลังสร้างลูกค้า...');
    let createdCustomers = 0;

    try {
      for (let i = 0; i < count; i++) {
        const name = customerNames[i % customerNames.length];
        const email = `customer${i + 1}@demo.com`;
        const password = 'Demo1234!';
        
        setProgress(`กำลังสร้างลูกค้า: ${name}...`);
        
        try {
          // สร้าง Firebase Auth user
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          const userId = userCredential.user.uid;
          
          // อัพเดท profile
          await updateProfile(userCredential.user, {
            displayName: name
          });
          
          // สร้าง user document
          await setDoc(doc(db, 'users', userId), {
            displayName: name,
            email: email,
            role: 'user',
            tcoins: {
              virtual: Math.floor(Math.random() * 2000), // 0-2000 Bonus Coins
              real: Math.floor(Math.random() * 3000) + 500 // 500-3500 T Coins
            },
            createdAt: new Date(),
            updatedAt: new Date()
          });
          
          createdCustomers++;
          
          // Sign out เพื่อสร้าง user ถัดไป
          await auth.signOut();
          
        } catch (error) {
          console.error(`Error creating customer ${i + 1}:`, error);
          // Continue with next customer
        }
      }
      
      setProgress(`✅ สร้างลูกค้า ${createdCustomers} คนสำเร็จ!`);
      setGeneratedCount(prev => ({ ...prev, users: createdCustomers }));
      
    } catch (error) {
      console.error('Error generating customers:', error);
      alert('เกิดข้อผิดพลาดในการสร้างลูกค้า: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">🌱 สร้างข้อมูล Demo สำหรับ Go Live</h2>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-yellow-800">
          <strong>⚠️ คำเตือน:</strong> เครื่องมือนี้จะสร้างร้านค้าและลูกค้า demo ในระบบจริง 
          ใช้เฉพาะเมื่อต้องการเตรียมข้อมูลสำหรับ go live เท่านั้น
        </p>
      </div>

      {/* สร้างร้านค้า */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">🏪 สร้างร้านค้า Demo</h3>
        <p className="text-sm text-gray-600 mb-4">
          สร้างร้านค้า {storeTemplates.length} ร้าน พร้อมสินค้า 3 รายการต่อร้าน
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => generateStores(storeTemplates.length)}
            disabled={loading}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'กำลังสร้าง...' : `สร้างร้านค้า ${storeTemplates.length} ร้าน`}
          </button>
        </div>
      </div>

      {/* สร้างลูกค้า */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">👥 สร้างลูกค้า Demo</h3>
        <p className="text-sm text-gray-600 mb-4">
          สร้างลูกค้า demo พร้อม T Coins และ Bonus Coins
        </p>
        <div className="flex gap-4 mb-4">
          <button
            onClick={() => generateCustomers(10)}
            disabled={loading}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'กำลังสร้าง...' : 'สร้างลูกค้า 10 คน'}
          </button>
          <button
            onClick={() => generateCustomers(20)}
            disabled={loading}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'กำลังสร้าง...' : 'สร้างลูกค้า 20 คน'}
          </button>
        </div>
      </div>

      {/* Progress */}
      {progress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">{progress}</p>
        </div>
      )}

      {/* Summary */}
      {generatedCount.stores > 0 || generatedCount.users > 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-2">📊 สรุปข้อมูลที่สร้างแล้ว:</h4>
          <ul className="text-sm text-green-700 space-y-1">
            <li>🏪 ร้านค้า: {generatedCount.stores} ร้าน</li>
            <li>📦 สินค้า: {generatedCount.products} รายการ</li>
            <li>👥 ลูกค้า: {generatedCount.users} คน</li>
          </ul>
        </div>
      ) : null}

      {/* ข้อมูล Login */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-6">
        <h4 className="font-semibold text-gray-800 mb-2">🔑 ข้อมูล Login:</h4>
        <p className="text-xs text-gray-600 mb-2">
          <strong>ร้านค้า:</strong> store1@demo.com ถึง store{storeTemplates.length}@demo.com<br />
          <strong>ลูกค้า:</strong> customer1@demo.com ถึง customer{generatedCount.users || 'N'}@demo.com<br />
          <strong>Password:</strong> Demo1234!
        </p>
      </div>
    </div>
  );
};

export default SeedDataGenerator;

